import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, Language, missingLanguages, Task } from '@quizdb/common';
import { Icon } from '../common/Icon';
import { formatMicroUsdFine } from '../job/formatCost';
import { LanguageCode } from './TaskBadges';

/** Props for `TranslationsCard`. */
export interface TranslationsCardProps {
  task: Task;
}

/**
 * A task's translations, one language at a time and read-only (QZ-D111), with its translation
 * state (QZ-D113).
 */
export const TranslationsCard = ({ task }: TranslationsCardProps) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Language>(task.originalLanguage);

  const others = (Object.keys(task.translations) as Language[]).filter(
    (language) => language !== task.originalLanguage
  );
  if (task.translation === undefined && others.length === 0) {
    return null;
  }

  // QZ-D113: a pending record on a non-approved task is never shown as translating.
  const translating = task.status === 'approved' && task.translation?.status === 'pending';
  const failed = task.translation?.status === 'failed';
  const missing = missingLanguages(task);

  const tabs = [
    task.originalLanguage,
    ...LANGUAGES.filter(
      (language) => language !== task.originalLanguage && task.targetLanguages.includes(language)
    ),
  ];
  const translation = task.translations[selected];

  return (
    <div className="qz-card">
      <div className="qz-card-header">
        <h3>{t('task.translations.title')}</h3>
        <span className={`qz-small ${failed ? 'text-danger' : 'qz-muted'} text-nowrap`}>
          {translating ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />
              {t('task.translations.pending')}
            </>
          ) : failed ? (
            <>
              <Icon name="triangle-exclamation" /> {t('task.translations.failed')}
            </>
          ) : (
            <>
              {t('task.translations.count', { count: others.length })}
              {task.translation && ` · ${formatMicroUsdFine(task.translation.microUsd)}`}
            </>
          )}
        </span>
      </div>
      <div className="qz-card-body">
        {failed && (
          <div className="alert alert-danger">
            <Icon name="circle-exclamation" />
            <div>
              {task.translation?.error && `${task.translation.error} `}
              {t('task.translations.retryHint')}
            </div>
          </div>
        )}

        <ul className="nav nav-tabs mb-3" role="tablist">
          {tabs.map((language) => {
            const hasTranslation = task.translations[language] !== undefined;
            const isOriginal = language === task.originalLanguage;
            const isSelected = language === selected;
            return (
              <li key={language} className="nav-item">
                <button
                  type="button"
                  role="tab"
                  id={`task-translation-tab-${language}`}
                  className={`nav-link${isSelected ? ' active' : ''}`}
                  aria-selected={isSelected}
                  aria-controls="task-translation-panel"
                  disabled={!hasTranslation}
                  onClick={() => hasTranslation && setSelected(language)}
                >
                  {isOriginal && (
                    <>
                      <Icon name="star" />
                      <span className="visually-hidden">{t('task.translations.original')}</span>
                    </>
                  )}
                  {translating && missing.includes(language) && <Icon name="hourglass-half" />}
                  <LanguageCode language={language} />
                </button>
              </li>
            );
          })}
        </ul>

        <div
          role="tabpanel"
          id="task-translation-panel"
          className="qz-stack"
          aria-labelledby={`task-translation-tab-${selected}`}
        >
          <p className="qz-question" style={{ fontSize: '1.2rem' }}>
            {translation?.text}
          </p>

          {translation?.choices && (
            <section>
              <h2 className="qz-section-title">{t('task.type.MultiChoiceQuestion')}</h2>
              <ul className="qz-answers">
                {translation.choices.map((choice, index) =>
                  index === 0 ? (
                    <li key={index} className="is-correct">
                      <Icon name="circle-check" />
                      <span className="visually-hidden">{t('task.field.correctChoice')}</span>{' '}
                      {choice}
                    </li>
                  ) : (
                    <li key={index}>
                      <span className="qz-answer-marker" aria-hidden="true">
                        {String.fromCharCode(65 + index)}
                      </span>
                      {choice}
                    </li>
                  )
                )}
              </ul>
            </section>
          )}

          {translation?.freetextAnswer !== undefined && (
            <section>
              <h2 className="qz-section-title">{t('task.type.FreetextQuestion')}</h2>
              <ul className="qz-answers">
                <li className="is-correct">
                  <Icon name="circle-check" />
                  {translation.freetextAnswer}
                </li>
              </ul>
            </section>
          )}

          {translation?.items && (
            <section>
              <h2 className="qz-section-title">{t('task.type.ItemOrdererer')}</h2>
              <ol className="qz-answers is-ordered">
                {translation.items.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ol>
            </section>
          )}

          {task.numericAnswer !== undefined && (
            <section>
              <h2 className="qz-section-title">{t('task.type.NumericTiebreakerQuestion')}</h2>
              <div className="qz-big-number">{task.numericAnswer}</div>
              <div className="qz-stat-hint">{t('task.translations.numbersNotTranslated')}</div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default TranslationsCard;
